import type { ApplicableLocation } from './locationUtils';

const MapPin = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="2.6" />
  </svg>
);

interface LocationRowProps {
  location: ApplicableLocation;
  selected: boolean;
  onSelect: (id: number) => void;
  rolesLabel: string;
  mapLabel: string;
  /** Pluralized "N interviews in progress"; omitted when the count is zero. */
  inProgressLabel?: string;
}

const LocationRow = ({
  location,
  selected,
  onSelect,
  rolesLabel,
  mapLabel,
  inProgressLabel,
}: LocationRowProps) => {
  const mapHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    location.address
  )}`;

  return (
    <div
      data-selected={selected}
      className="group flex items-center gap-3 rounded-xl border-2 border-[#e3e4e8] bg-white p-3 data-[selected=true]:border-[var(--bg-color)] data-[selected=true]:bg-[var(--bg-color-shade)]"
    >
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
        <input
          type="radio"
          name="client-location"
          className="sr-only"
          checked={selected}
          onChange={() => onSelect(location.id)}
        />
        <span
          className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-data-[selected=true]:border-[var(--bg-color)]"
        >
          <span className="size-2.5 scale-0 rounded-full bg-[var(--bg-color)] opacity-0 transition-all group-data-[selected=true]:scale-100 group-data-[selected=true]:opacity-100" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-[#16171a]">
            {location.name}
          </span>
          <span className="block truncate text-sm text-[#6b7079]">
            {location.address}
          </span>
          <span className="mt-0.5 block text-xs font-semibold text-[#16171a]">
            {rolesLabel}
          </span>
          {location.inProgressCount > 0 && inProgressLabel && (
            <span className="mt-1 flex w-fit items-center rounded-md bg-[var(--bg-color-shade)] px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--bg-color-active)]">
              {inProgressLabel}
            </span>
          )}
        </span>
      </label>
      <a
        href={mapHref}
        target="_blank"
        rel="noopener noreferrer"
        className="flex shrink-0 flex-col items-center gap-1 text-[var(--bg-color-active)]"
        aria-label={mapLabel}
      >
        <span className="flex size-9 items-center justify-center rounded-lg bg-[var(--bg-color-shade)]">
          <MapPin />
        </span>
        <span className="text-xs font-semibold text-[#6b7079]">{mapLabel}</span>
      </a>
    </div>
  );
};

export default LocationRow;
